import React, { useCallback, useEffect } from 'react';
import { DxCombobox } from '@dvag/design-system-react';
import type { ComboboxSearchController, DxComboboxCustomEvent } from '@dvag/design-system/loader';
import { RefCallBack, RegisterOptions } from 'react-hook-form';

import { FondCollectionItem } from '../types';
import { FondFieldType } from 'utils/fieldList';

export interface FundsComboboxProps {
  id?: string;
  allFunds: FondCollectionItem[];
  label: string;
  disabledIsins?: string[];
  selectedFund?: { fondName?: string; isin?: string; label?: string };
  preSearchLetterCount?: number;
  onFundSelect: ({ fondName, isin }: { fondName: string; isin: string }) => void;
  register: (
    fieldName: string,
    resourceIndex?: number,
    options?: RegisterOptions,
  ) => {
    onValueChange: (e: { detail: unknown }) => void;
    errormessage: string;
    value: string;
    resourceFieldName: string;
    ref: RefCallBack;
  };
  resourceIndex: number;
}

const normalize = (str: string) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const includes = (source: string, substr: string) => normalize(source).includes(normalize(substr));

export const FundsCombobox = ({
  id = 'funds-combobox',
  allFunds,
  label,
  selectedFund,
  disabledIsins = [],
  preSearchLetterCount = 1,
  register,
  onFundSelect,
  resourceIndex,
}: FundsComboboxProps) => {
  const handleAutocomplete = useCallback(
    (e: DxComboboxCustomEvent<ComboboxSearchController>) => {
      const query = e.detail.value.trim();

      if (query.length < preSearchLetterCount) {
        e.detail.provideValues([]);
        return;
      }

      const options = allFunds
        .filter(
          ({ fondName, isin }) =>
            !disabledIsins.includes(isin) && (includes(fondName, query) || includes(isin, query)),
        )
        .map(({ fondName, isin }) => ({
          isin,
          label: fondName,
          subLabel: `ISIN: ${isin}`,
        }));

      e.detail.provideValues(options.length ? [{ label: undefined, options }] : []);
    },
    [allFunds, preSearchLetterCount, disabledIsins],
  );

  const handleSelect = useCallback(
    (e: DxComboboxCustomEvent<{ label: string; isin: string }>) => {
      if (e.detail?.label) {
        onFundSelect({ fondName: e.detail.label, isin: e.detail.isin });
      }
    },
    [onFundSelect],
  );

  const onInputChange = useCallback(
    (e: CustomEvent) => {
      if (e.detail.reason === 'clear' && !e.detail.value) {
        onFundSelect({ fondName: '', isin: '' });
      }
    },
    [allFunds, onFundSelect],
  );

  useEffect(() => {
    const el: HTMLDxComboboxElement | null = document.querySelector(`#${id}`);
    el?.addEventListener('search', handleAutocomplete);
    return () => el?.removeEventListener('search', handleAutocomplete);
  }, [id, handleAutocomplete]);

  return (
    <DxCombobox
      required
      id={id}
      data-testid={`${id}-combo`}
      usagemode="new"
      label={label}
      value={selectedFund || { fondName: '', isin: '', label: '' }}
      errormessage={register(FondFieldType.fondName, resourceIndex).errormessage}
      presearchlettercount={preSearchLetterCount}
      onInputChange={onInputChange}
      onSelectSearchOption={handleSelect}
    />
  );
};
